<template>
  <div id="productCatalogPage">
    <div class="header">
      <el-row>
        <el-col :span="18">
          <div class="title">Product Catalog</div>
          <div class="subtitle">Products and collections available in Growlytics.</div>
        </el-col>
        <el-col :span="6" class="headerActions">
          <el-button size="small" type="primary" :loading="refreshing" @click="refreshPage">
            <i class="el-icon-refresh"></i>
            Refresh
          </el-button>
        </el-col>
      </el-row>
    </div>

    <el-row :gutter="12" class="summaryRow" v-loading="loadingSummary">
      <el-col :xs="24" :sm="12" :md="6">
        <div class="summaryItem">
          <div class="summaryLabel">Products</div>
          <div class="summaryValue">{{ formatCount(summary && summary.products_count) }}</div>
        </div>
      </el-col>
      <el-col :xs="24" :sm="12" :md="6">
        <div class="summaryItem">
          <div class="summaryLabel">Collections</div>
          <div class="summaryValue">{{ formatCount(summary && summary.collections_count) }}</div>
        </div>
      </el-col>
      <el-col :xs="24" :sm="12" :md="6">
        <div class="summaryItem">
          <div class="summaryLabel">Archived Products</div>
          <div class="summaryValue">{{ formatCount(summary && summary.archived_products_count) }}</div>
        </div>
      </el-col>
      <el-col :xs="24" :sm="12" :md="6">
        <div class="summaryItem">
          <div class="summaryLabel">Archived Collections</div>
          <div class="summaryValue">{{ formatCount(summary && summary.archived_collections_count) }}</div>
        </div>
      </el-col>
    </el-row>

    <div class="catalogPanel">
      <el-tabs v-model="activeTab" @tab-click="onTabChange">
        <el-tab-pane label="Products" name="products">
          <div class="filters">
            <el-input class="searchInput" prefix-icon="el-icon-search" size="small" v-model="productFilters.keyword" @input="onProductSearch" placeholder="Search products"> </el-input>
            <el-select class="filterSelect" size="small" v-model="productFilters.status" @change="resetProductPaginationAndFetch" placeholder="Status">
              <el-option v-for="option in statusOptions" :key="option.label" :label="option.label" :value="option.value"> </el-option>
            </el-select>
            <el-select class="filterSelect" size="small" v-model="productFilters.isArchived" @change="resetProductPaginationAndFetch" placeholder="Archived">
              <el-option v-for="option in archivedOptions" :key="option.label" :label="option.label" :value="option.value"> </el-option>
            </el-select>
            <el-select class="pageSizeSelect" size="small" v-model="productPageOptions.pageSize" @change="onProductPageSizeChange" placeholder="Page size">
              <el-option v-for="size in pageSizeOptions" :key="size" :label="size + ' / page'" :value="size"> </el-option>
            </el-select>
          </div>

          <div class="tableWrap" v-loading="loadingProducts">
            <el-table v-if="products && products.length > 0" :data="products" row-key="id" size="small">
              <el-table-column label="Image" width="70">
                <template slot-scope="scope">
                  <div class="thumbnail" :class="{ emptyThumbnail: !scope.row.image }">
                    <img v-if="scope.row.image" :src="scope.row.image" />
                    <i v-else class="el-icon-picture-outline"></i>
                  </div>
                </template>
              </el-table-column>
              <el-table-column label="Product" min-width="260">
                <template slot-scope="scope">
                  <div class="primaryText">{{ scope.row.name || 'N/A' }}</div>
                  <div class="mutedText">{{ scope.row.product_id }}</div>
                </template>
              </el-table-column>
              <el-table-column label="Vendor" prop="vendor" width="140">
                <template slot-scope="scope">{{ scope.row.vendor || 'N/A' }}</template>
              </el-table-column>
              <el-table-column label="Type" prop="type" width="140">
                <template slot-scope="scope">{{ scope.row.type || 'N/A' }}</template>
              </el-table-column>
              <el-table-column label="Price" width="110">
                <template slot-scope="scope">{{ formatPrice(scope.row.price) }}</template>
              </el-table-column>
              <el-table-column label="Status" width="110">
                <template slot-scope="scope">
                  <el-tag size="mini" :type="getStatusType(scope.row.status)">{{ getStatusLabel(scope.row.status) }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column label="Collections" width="110">
                <template slot-scope="scope">{{ formatCount(scope.row.collections_count) }}</template>
              </el-table-column>
              <el-table-column label="Updated" width="170">
                <template slot-scope="scope">{{ formatTime(scope.row.updated_at) }}</template>
              </el-table-column>
              <el-table-column label="Actions" width="125" fixed="right">
                <template slot-scope="scope">
                  <el-button size="mini" type="text" @click="fetchProductDetail(scope.row)">Details</el-button>
                  <el-button v-if="scope.row.url" size="mini" type="text" @click="openUrl(scope.row.url)">
                    <i class="el-icon-link"></i>
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
            <div class="emptyState" v-if="!loadingProducts && (!products || products.length == 0)">
              {{ getProductEmptyMessage() }}
            </div>
          </div>

          <div class="pagination" v-if="productPageOptions.total > 0">
            <div class="totalCount">Total {{ formatCount(productPageOptions.total) }} products</div>
            <el-pagination @current-change="onProductPageChange" :current-page="productPageOptions.pageNumber" :page-size="productPageOptions.pageSize" layout="prev, pager, next" :total="productPageOptions.total"></el-pagination>
          </div>
        </el-tab-pane>

        <el-tab-pane label="Collections" name="collections">
          <div class="filters">
            <el-input class="searchInput" prefix-icon="el-icon-search" size="small" v-model="collectionFilters.keyword" @input="onCollectionSearch" placeholder="Search collections"> </el-input>
            <el-select class="filterSelect" size="small" v-model="collectionFilters.isDynamic" @change="resetCollectionPaginationAndFetch" placeholder="Type">
              <el-option v-for="option in collectionTypeOptions" :key="option.label" :label="option.label" :value="option.value"> </el-option>
            </el-select>
            <el-select class="filterSelect" size="small" v-model="collectionFilters.isArchived" @change="resetCollectionPaginationAndFetch" placeholder="Archived">
              <el-option v-for="option in archivedOptions" :key="option.label" :label="option.label" :value="option.value"> </el-option>
            </el-select>
            <el-select class="pageSizeSelect" size="small" v-model="collectionPageOptions.pageSize" @change="onCollectionPageSizeChange" placeholder="Page size">
              <el-option v-for="size in pageSizeOptions" :key="size" :label="size + ' / page'" :value="size"> </el-option>
            </el-select>
          </div>

          <div class="tableWrap" v-loading="loadingCollections">
            <el-table v-if="collections && collections.length > 0" :data="collections" row-key="id" size="small">
              <el-table-column label="Image" width="70">
                <template slot-scope="scope">
                  <div class="thumbnail" :class="{ emptyThumbnail: !scope.row.image }">
                    <img v-if="scope.row.image" :src="scope.row.image" />
                    <i v-else class="el-icon-picture-outline"></i>
                  </div>
                </template>
              </el-table-column>
              <el-table-column label="Collection" min-width="280">
                <template slot-scope="scope">
                  <div class="primaryText">{{ scope.row.name || 'N/A' }}</div>
                  <div class="mutedText">{{ scope.row.collection_id }}</div>
                </template>
              </el-table-column>
              <el-table-column label="Type" width="110">
                <template slot-scope="scope">
                  <el-tag size="mini" type="info">{{ getCollectionTypeLabel(scope.row) }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column label="Products" width="110">
                <template slot-scope="scope">{{ formatCount(scope.row.products_count) }}</template>
              </el-table-column>
              <el-table-column label="State" width="110">
                <template slot-scope="scope">
                  <el-tag size="mini" :type="getArchiveType(scope.row.is_archived)">{{ getArchiveLabel(scope.row.is_archived) }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column label="Updated" width="170">
                <template slot-scope="scope">{{ formatTime(scope.row.updated_at) }}</template>
              </el-table-column>
              <el-table-column label="Actions" width="135" fixed="right">
                <template slot-scope="scope">
                  <el-button size="mini" type="text" @click="fetchCollectionDetail(scope.row)">Products</el-button>
                  <el-button v-if="scope.row.url" size="mini" type="text" @click="openUrl(scope.row.url)">
                    <i class="el-icon-link"></i>
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
            <div class="emptyState" v-if="!loadingCollections && (!collections || collections.length == 0)">
              {{ getCollectionEmptyMessage() }}
            </div>
          </div>

          <div class="pagination" v-if="collectionPageOptions.total > 0">
            <div class="totalCount">Total {{ formatCount(collectionPageOptions.total) }} collections</div>
            <el-pagination @current-change="onCollectionPageChange" :current-page="collectionPageOptions.pageNumber" :page-size="collectionPageOptions.pageSize" layout="prev, pager, next" :total="collectionPageOptions.total"></el-pagination>
          </div>
        </el-tab-pane>
      </el-tabs>
    </div>

    <el-drawer class="catalogDrawer" title="Product Details" :visible="productDrawerVisible" :size="drawerSize" append-to-body @close="productDrawerVisible = false">
      <div v-loading="loadingProductDetail" class="drawerContent" v-if="productDetailProduct">
        <div class="drawerHeader productDrawerHeader">
          <div class="productDetailImage" :class="{ emptyThumbnail: !productDetailProduct.image }">
            <img v-if="productDetailProduct.image" :src="productDetailProduct.image" />
            <i v-else class="el-icon-picture-outline"></i>
          </div>
          <div class="drawerHeaderInfo">
            <div class="drawerTitle">{{ productDetailProduct.name || 'N/A' }}</div>
            <div class="mutedText">{{ productDetailProduct.product_id }}</div>
            <el-tag size="mini" :type="getStatusType(productDetailProduct.status)">{{ getStatusLabel(productDetailProduct.status) }}</el-tag>
            <el-tag size="mini" :type="getArchiveType(productDetailProduct.is_archived)">{{ getArchiveLabel(productDetailProduct.is_archived) }}</el-tag>
          </div>
        </div>

        <div class="detailGrid">
          <div class="detailItem"><span>Vendor</span>{{ productDetailProduct.vendor || 'N/A' }}</div>
          <div class="detailItem"><span>Type</span>{{ productDetailProduct.type || 'N/A' }}</div>
          <div class="detailItem"><span>Price</span>{{ formatPrice(productDetailProduct.price) }}</div>
          <div class="detailItem"><span>Original Price</span>{{ formatPrice(productDetailProduct.original_higher_price) }}</div>
          <div class="detailItem"><span>Created</span>{{ formatTime(productDetailProduct.created_at) }}</div>
          <div class="detailItem"><span>Updated</span>{{ formatTime(productDetailProduct.updated_at) }}</div>
        </div>

        <div class="detailSection">
          <div class="sectionTitle">Description</div>
          <div class="descriptionText">{{ productDetailProduct.description || 'N/A' }}</div>
        </div>

        <div class="detailSection">
          <div class="sectionTitle">Collections</div>
          <div class="drawerListItem" v-for="collection in productDetailCollections" :key="collection.id">
            <div>
              <div class="primaryText">{{ collection.name || 'N/A' }}</div>
              <div class="mutedText">{{ collection.collection_id }}</div>
            </div>
            <el-tag size="mini" type="info">{{ getCollectionTypeLabel(collection) }}</el-tag>
          </div>
          <div class="emptyInline" v-if="productDetailCollections.length == 0">No collections found for this product.</div>
        </div>
      </div>
    </el-drawer>

    <el-drawer class="catalogDrawer collectionDrawer" title="Collection Details" :visible="collectionDrawerVisible" :size="collectionDrawerSize" append-to-body @close="collectionDrawerVisible = false">
      <div v-loading="loadingCollectionDetail" class="drawerContent" v-if="collectionDetailCollection">
        <div class="drawerHeader">
          <div class="largeThumbnail" :class="{ emptyThumbnail: !collectionDetailCollection.image }">
            <img v-if="collectionDetailCollection.image" :src="collectionDetailCollection.image" />
            <i v-else class="el-icon-picture-outline"></i>
          </div>
          <div>
            <div class="drawerTitle">{{ collectionDetailCollection.name || 'N/A' }}</div>
            <div class="mutedText">{{ collectionDetailCollection.collection_id }}</div>
            <el-tag size="mini" type="info">{{ getCollectionTypeLabel(collectionDetailCollection) }}</el-tag>
            <el-tag size="mini" :type="getArchiveType(collectionDetailCollection.is_archived)">{{ getArchiveLabel(collectionDetailCollection.is_archived) }}</el-tag>
          </div>
        </div>

        <div class="detailGrid">
          <div class="detailItem"><span>Products</span>{{ formatCount(collectionDetailCollection.products_count) }}</div>
          <div class="detailItem"><span>Created</span>{{ formatTime(collectionDetailCollection.created_at) }}</div>
          <div class="detailItem"><span>Updated</span>{{ formatTime(collectionDetailCollection.updated_at) }}</div>
        </div>

        <div class="detailSection">
          <div class="sectionTitle">Products</div>
          <div class="drawerFilters">
            <el-input class="drawerSearchInput" prefix-icon="el-icon-search" size="small" v-model="collectionProductFilters.keyword" @input="onCollectionProductSearch" placeholder="Search products"> </el-input>
            <el-select class="drawerFilterSelect" size="small" v-model="collectionProductFilters.status" @change="resetCollectionProductPaginationAndFetch" placeholder="Status">
              <el-option v-for="option in statusOptions" :key="option.label" :label="option.label" :value="option.value"> </el-option>
            </el-select>
            <el-select class="drawerFilterSelect" size="small" v-model="collectionProductFilters.isArchived" @change="resetCollectionProductPaginationAndFetch" placeholder="Archived">
              <el-option v-for="option in archivedOptions" :key="option.label" :label="option.label" :value="option.value"> </el-option>
            </el-select>
          </div>

          <div v-loading="loadingCollectionProducts" class="drawerTableWrap">
            <el-table v-if="collectionProducts && collectionProducts.length > 0" :data="collectionProducts" size="mini">
              <el-table-column label="Product" min-width="240">
                <template slot-scope="scope">
                  <div class="primaryText">{{ scope.row.name || 'N/A' }}</div>
                  <div class="mutedText">{{ scope.row.product_id }}</div>
                </template>
              </el-table-column>
              <el-table-column label="Status" width="105">
                <template slot-scope="scope">
                  <el-tag size="mini" :type="getStatusType(scope.row.status)">{{ getStatusLabel(scope.row.status) }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column label="Price" width="100">
                <template slot-scope="scope">{{ formatPrice(scope.row.price) }}</template>
              </el-table-column>
            </el-table>
            <div class="emptyInline" v-if="!loadingCollectionProducts && (!collectionProducts || collectionProducts.length == 0)">No products are currently mapped to this collection.</div>
          </div>

          <div class="drawerPagination" v-if="collectionProductPageOptions.total > 0">
            <el-select class="miniPageSizeSelect" size="mini" v-model="collectionProductPageOptions.pageSize" @change="onCollectionProductPageSizeChange">
              <el-option v-for="size in [10, 25, 50]" :key="size" :label="size + ' / page'" :value="size"> </el-option>
            </el-select>
            <el-pagination small @current-change="onCollectionProductPageChange" :current-page="collectionProductPageOptions.pageNumber" :page-size="collectionProductPageOptions.pageSize" layout="prev, pager, next" :total="collectionProductPageOptions.total"></el-pagination>
          </div>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<style lang="scss" src="./productCatalog.scss"></style>

<script>
import productCatalogComponent from './productCatalogComponent';
export default productCatalogComponent;
</script>
